import type { MouseEvent, ReactNode } from 'react';
import type { DataTableColumn } from '../types';
import { buildColumn, EMPTY_VALUE, textOf, type ColumnPresetOptions } from './base';

export type TextColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    /** The cell's text. `null`/`undefined` renders `fallback`. */
    value: (row: T) => string | number | null | undefined;
    /** Shown when `value` is null/undefined/empty. Defaults to an em dash. */
    fallback?: ReactNode;
    /** Classes on the wrapping `<span>`. Omitted, the text is rendered bare. */
    className?: string;
};

/**
 * Plain text from an accessor.
 *
 * `truncate` defaults to **on** (text is the one thing that always wants
 * clipping over wrapping) and, while on, the cell reveals the full value on
 * hover via `getTitle` (the shared Radix Tooltip) so overflowing text stays
 * readable. Pass `truncate: false` for wrapping text, or your own `getTitle`
 * to override.
 */
export function textColumn<T, K extends string = string>(
    options: TextColumnOptions<T, K>
): DataTableColumn<T, K> {
    const { value, fallback = EMPTY_VALUE, className } = options;
    const truncate = options.truncate ?? true;

    return buildColumn(
        options,
        (row) => {
            const text = textOf(value(row));
            if (!text) {
                return fallback;
            }
            return className ? <span className={className}>{text}</span> : text;
        },
        {
            truncate,
            getTitle: truncate ? (row) => textOf(value(row)) || undefined : undefined
        }
    );
}

export type LinkColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    /** The anchor's target. A null href degrades to plain text. */
    href: (row: T) => string | null | undefined;
    /** Link text. Defaults to the href. */
    value?: (row: T) => string | number | null | undefined;
    /** Opens in a new tab with `rel="noopener noreferrer"`. */
    external?: boolean;
    /** Extra side effect on click (analytics, a router push, …). */
    onClick?: (row: T, event: MouseEvent<HTMLAnchorElement>) => void;
    fallback?: ReactNode;
    className?: string;
};

const LINK_CLASSES = 'text-blue-600 hover:underline focus:outline-none focus-visible:underline';

/**
 * An anchor built from an `href` accessor.
 *
 * `stopClickPropagation` defaults to **on**: inside a clickable row, following
 * a link must not also fire the row's action.
 */
export function linkColumn<T, K extends string = string>(
    options: LinkColumnOptions<T, K>
): DataTableColumn<T, K> {
    const { href, value, external = false, onClick, fallback = EMPTY_VALUE, className } = options;
    const truncate = options.truncate ?? true;
    const textFor = (row: T): string => textOf(value ? value(row) : href(row));

    return buildColumn(
        options,
        (row) => {
            const target = href(row);
            const text = textFor(row) || target;
            if (!target) {
                return fallback;
            }
            return (
                <a
                    href={target}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noopener noreferrer' : undefined}
                    onClick={onClick ? (event) => onClick(row, event) : undefined}
                    className={className ? `${LINK_CLASSES} ${className}` : LINK_CLASSES}
                >
                    {text}
                </a>
            );
        },
        {
            truncate,
            stopClickPropagation: true,
            getTitle: truncate ? (row) => textFor(row) || undefined : undefined
        }
    );
}
